<template>
  <v-dialog :value="value" max-width="700" scrollable @input="$emit('input', $event)">
    <v-card class="bundle_dialog">
      <v-card-title class="bundle_dialog__title">
        <span>
          {{ $t("TITLES.package_details") }} -
          {{ pkg?.package_number }}
        </span>
        <v-spacer />
        <v-btn icon @click="$emit('input', false)">
          <i class="fal fa-times"></i>
        </v-btn>
      </v-card-title>

      <v-card-text v-if="pkg">
        <v-row>
          <v-col cols="12" md="6">
            <div class="info_row">
              <span class="info_row__label">{{ $t("PLACEHOLDERS.package_number") }}</span>
              <span class="info_row__value">{{ pkg.package_number }}</span>
            </div>
            <div class="info_row">
              <span class="info_row__label">{{ $t("PLACEHOLDERS.package_status") }}</span>
              <v-chip
                small
                :color="statusColor"
                text-color="white"
                class="ms-1"
              >
                {{ statusLabel }}
              </v-chip>
            </div>
            <div class="info_row">
              <span class="info_row__label">{{ $t("PLACEHOLDERS.bundle_number") }}</span>
              <span class="info_row__value">{{ pkg.bundle_number }}</span>
            </div>
            <div class="info_row">
              <span class="info_row__label">{{ $t("PLACEHOLDERS.client_name") }}</span>
              <span class="info_row__value">{{ pkg.user?.name || "-" }}</span>
            </div>
            <div class="info_row">
              <span class="info_row__label">{{ $t("PLACEHOLDERS.contact_number") }}</span>
              <span class="info_row__value" dir="ltr">{{ pkg.user?.mobile || "-" }}</span>
            </div>
            <div class="info_row">
              <span class="info_row__label">{{ $t("PLACEHOLDERS.bundle_city") }}</span>
              <span class="info_row__value">{{ pkg.city || "-" }}</span>
            </div>
            <div class="info_row">
              <span class="info_row__label">{{ $t("PLACEHOLDERS.address") }}</span>
              <span class="info_row__value">{{ pkg.address || "-" }}</span>
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="info_row">
              <span class="info_row__label">{{ $t("PLACEHOLDERS.package_weight") }}</span>
              <span class="info_row__value">
                {{ pkg.weight || "-" }} {{ $t("PLACEHOLDERS.kg") }}
              </span>
            </div>
            <div class="info_row">
              <span class="info_row__label">{{ $t("PLACEHOLDERS.package_dimensions") }}</span>
              <span class="info_row__value">{{ pkg.dimensions || "-" }}</span>
            </div>
            <div class="info_row">
              <span class="info_row__label">{{ $t("PLACEHOLDERS.delivery_type") }}</span>
              <span class="info_row__value">{{ serviceTypeLabel }}</span>
            </div>
            <div class="info_row">
              <span class="info_row__label">{{ $t("PLACEHOLDERS.bundle_package_price") }}</span>
              <span class="info_row__value">
                {{ pkg.delivery_fee || 0 }} {{ $t("PLACEHOLDERS.sar_currency") }}
              </span>
            </div>
            <div class="info_row">
              <span class="info_row__label">{{ $t("PLACEHOLDERS.provider_name") }}</span>
              <span class="info_row__value">{{ pkg.provider?.name || "-" }}</span>
            </div>
            <div class="info_row" v-if="pkg.notes">
              <span class="info_row__label">{{ $t("PLACEHOLDERS.notes") }}</span>
              <span class="info_row__value">{{ pkg.notes }}</span>
            </div>
            <div class="info_row" v-if="pkg.last_update">
              <span class="info_row__label">{{ $t("PLACEHOLDERS.last_update") }}</span>
              <span class="info_row__value">{{ pkg.last_update }}</span>
            </div>
          </v-col>
        </v-row>

        <div class="text-center my-3" v-if="pkg.qr">
          <img :src="pkg.qr" :alt="pkg.package_number" style="max-width: 150px" />
        </div>

        <div v-if="pkg.return_source" class="mt-3">
          <h6 class="section_title mb-2">{{ $t("TITLES.return_info") }}</h6>
          <v-divider class="mb-2" />
          <div class="info_row">
            <span class="info_row__label">{{ $t("PLACEHOLDERS.return_source") }}</span>
            <span class="info_row__value">{{ pkg.return_source }}</span>
          </div>
          <div class="info_row">
            <span class="info_row__label">{{ $t("PLACEHOLDERS.return_fee") }}</span>
            <span class="info_row__value">
              {{ pkg.return_fee }} {{ $t("PLACEHOLDERS.sar_currency") }}
            </span>
          </div>
          <div class="info_row" v-if="pkg.return_requested_at">
            <span class="info_row__label">{{ $t("PLACEHOLDERS.return_requested_at") }}</span>
            <span class="info_row__value">{{ pkg.return_requested_at }}</span>
          </div>
          <div class="info_row" v-if="pkg.returned_at">
            <span class="info_row__label">{{ $t("PLACEHOLDERS.returned_at") }}</span>
            <span class="info_row__value">{{ pkg.returned_at }}</span>
          </div>
        </div>

        <div v-if="pkg.history && pkg.history.length" class="mt-3">
          <package-history-panel :history="pkg.history" :default-open="true" />
        </div>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn text @click="$emit('input', false)">
          {{ $t("BUTTONS.close") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  getPackageStatusColor,
  getServiceTypeLabel,
} from "../utils/bundleHelpers";
import PackageHistoryPanel from "./PackageHistoryPanel.vue";

export default {
  name: "BundlePackageDetailsDialog",

  components: {
    PackageHistoryPanel,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    pkg: {
      type: Object,
      default: null,
    },
  },

  computed: {
    statusColor() {
      return getPackageStatusColor(this.pkg?.status);
    },
    statusLabel() {
      return this.pkg?.status_obj?.text || this.pkg?.status;
    },
    serviceTypeLabel() {
      return getServiceTypeLabel(this.pkg?.service_type, this.$t.bind(this));
    },
  },
};
</script>

<style scoped lang="scss">
.bundle_dialog {
  &__title {
    background-color: var(--very_light_theme_clr);
    color: var(--theme_text_clr);
    border-bottom: 1px solid var(--border_clr);
  }
}

.info_row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;

  &__label {
    color: var(--light_gray_clr);
    min-width: 130px;
  }

  &__value {
    color: var(--theme_text_clr);
    font-family: $medium_font;
  }
}

.section_title {
  color: var(--main_theme_clr);
  font-family: $semi_bold_font;
}
</style>
